import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Components
import {
  Button,
  Link,
  InputField,
  Modal,
  SupportingMessage,
} from "@octopus-investments/deep-sea-components";
import Portal from "components/Portal/Portal";

// Actions
import { setContent, setEmailAddress } from "store/slices/applicationSlice";
import { setCONReminderButtonLoader } from "store/slices/loadersSlice";
import { remindCON } from "store/slices/accountSlice";

// Tools
import { capitalizeFirstLetter, focusLastInputFieldCharacter, validateEmail } from "utils/helpers";
import { VALIDATION_ERRORS, SYSTEM_ERRORS } from "utils/constants";

// Tracking
import { ampli } from "ampli";

// Styles
import "./CONReminder.scss";

export default function CONReminder({ fromError = false, firstLetterCapitalised = false }) {
  const { email: emailAddress } = useSelector(({ application }) => application);
  const { isConReminderButtonLoader } = useSelector(({ loaders }) => loaders);

  const [email, setEmail] = useState(emailAddress);

  const [showModal, setShowModal] = useState(false);
  const [showDone, setShowDone] = useState(false);

  const [showValidationError, setShowValidationError] = useState(false);
  const [showSystemError, setShowSystemError] = useState(false);
  const [statusCode, setStatusCode] = useState(undefined);

  const CON_REMINDER_MODAL_LINK_TEXT = "send an account number reminder";

  // Redux
  const reduxDispatch = useDispatch();

  const handleEmailFieldChange = e => {
    if (showValidationError) setShowValidationError(false);
    if (showSystemError) setShowSystemError(false);

    setEmail(e.value);
  };

  const handleEmailFieldKeyPress = e => {
    if (e.which === 13 && !isConReminderButtonLoader) handleSendReminderClicked();
  };

  const handleSendReminderClicked = () => {
    if (isConReminderButtonLoader) return;

    const isEmailValid = Boolean(validateEmail(email));
    setShowValidationError(!isEmailValid);

    ampli.sendAccountNumberReminderSent({
      di_email_entered: email,
      di_email_criteria_met: isEmailValid,
    });

    if (!isEmailValid) return;

    reduxDispatch(setCONReminderButtonLoader(true));

    reduxDispatch(remindCON({ email })).then(result => {
      const { success, status } = result.payload;

      if (success) {
        setShowDone(true);
      } else {
        setShowSystemError(true);
        setStatusCode(status);
        return reduxDispatch(setCONReminderButtonLoader(false));
      }
    });
  };

  const handleCancelButtonClicked = () => {
    focusLastInputFieldCharacter("confirmCONPageCONInput");
    setShowModal(!showModal);
    setShowDone(false);

    ampli.sendAccountNumberReminderCancelled({
      di_email_entered: email,
    });
  };

  const handleClickToLoginLink = () => {
    reduxDispatch(setEmailAddress(email));

    ampli.loginLinkClicked({
      di_email_entered: email,
      location: "di_send_account_number_reminder_modal",
    });

    reduxDispatch(setContent("login"));
  };

  const handleCONReminder = () => {
    ampli.sendAccountNumberReminderClicked({
      di_email_entered: email,
      location: fromError ? "di_cant_find_account_error" : "di_confirm_account_number",
    });

    setShowModal(true);
  };

  const renderConfirmationBlock = () => {
    return (
      <>
        <h3 className="coheadline">Account number reminder sent</h3>
        <div className="information-sent-block">
          <p>
            We just sent a reminder to <strong>{email}</strong>.
          </p>
          <div className="button-block">
            <Button onClick={handleCancelButtonClicked} label="Done" />
          </div>
        </div>
      </>
    );
  };

  const renderEnterEmailField = () => {
    const renderEmailServiceError = () => {
      return SYSTEM_ERRORS["424"];
    };

    const renderEmailExistsError = () => {
      return (
        <>
          {VALIDATION_ERRORS["emailExists"]}{" "}
          <Link isInline onLinkClick={handleClickToLoginLink}>
            Please click here to login
          </Link>
        </>
      );
    };

    let errorMsg;
    if (showSystemError) {
      if (statusCode === 424) {
        errorMsg = renderEmailServiceError();
      } else {
        errorMsg = renderEmailExistsError();
      }
    } else {
      errorMsg = VALIDATION_ERRORS["wrongPattern"];
    }

    const showError = showSystemError || showValidationError;

    return (
      <>
        <h3 className="coheadline">Send an account number reminder</h3>
        <div className="enter-your-email-block">
          <InputField
            autoComplete="off"
            autoFocus
            autocompletetype="email"
            className="client-email"
            value={email}
            error={showError}
            label="Email address"
            name="email"
            onChange={handleEmailFieldChange}
            onKeyPress={handleEmailFieldKeyPress}
            placeholder="Your email"
            required
            showError={showError}
            type="email"
          />
          {showError && <SupportingMessage status="error" message={errorMsg} />}
          <div className="buttons-block">
            <Button
              onClick={handleCancelButtonClicked}
              id="conReminderCancel"
              label="Cancel"
              variant="outline"
            />
            <Button
              id="conReminderSend"
              isLoading={isConReminderButtonLoader}
              label="Send reminder"
              onClick={handleSendReminderClicked}
              variant="solid"
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <Link isInline onLinkClick={() => handleCONReminder()}>
        {firstLetterCapitalised
          ? capitalizeFirstLetter(CON_REMINDER_MODAL_LINK_TEXT)
          : CON_REMINDER_MODAL_LINK_TEXT}
      </Link>
      {showModal && (
        <Portal>
          <Modal
            className="con-reminder-modal"
            id="conReminderModal"
            handleCloseModal={handleCancelButtonClicked}
            showButtons={!showDone}
            showCloseIcon={false}
            showModal={showModal}
          >
            {showDone ? renderConfirmationBlock() : renderEnterEmailField()}
          </Modal>
        </Portal>
      )}
    </>
  );
}
